import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginationResponse } from '@ip-only/front-end-library';
import { forkJoin, map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { validateFileResponse } from '../../shared/util/download-file.helper';
import {
  BuildingInstallationReport,
  InstallationReportOverview,
  DeliveryPointBomRequest,
  DeliveryPointInstallationReport,
  GroupAgreementDetails,
  GroupAgreementInstallationReport,
} from '../entities/installation-reporting-mdu-details.entities';
import {
  CableBlowingV2Response,
  CommissionV2Response,
  DuctConnectingV2Response,
  InstallationReportV2Request,
  DeliveryPointInstallationReportV2Request,
  ListWrapperEntity,
  MeasuringV2Response,
  PatchPreInstallationV2Request,
  PlotDesignV2Response,
  PlotExcavationV2Response,
  PreInstallationV2Response,
  SectionNotApplicableV3Request,
  SectionV2Response,
  SelfInspectionV2Request,
  SelfInspectionV2Response,
  SelfInspectionDefinitionV2Response,
  FileMetaV2Response,
} from './installation-reporting-mdu-modal/model/installation-reporting-mdu-bff.entities';
import { InstallationReportSectionId } from './installation-reporting-mdu-modal/model/installation-reporting-mdu-section.types';
const installationReportingV3Url = environment.installationReportingApiV3Url;
const installationReportingV2Url = environment.installationReportingApiV2Url;

const definitionSectionBySection: Partial<
  Record<InstallationReportSectionId, string>
> = {
  PLOT_DESIGN: 'PLOT_DESIGN',
  PRE_INSTALLATION: 'PRE_INSTALLATION',
  PLOT_EXCAVATION: 'PLOT_EXCAVATION',
  DUCT_CONNECTING: 'DUCT_CONNECTING',
  CABLE_BLOWING: 'CABLE_BLOWING',
  SPLICING_MEASURING: 'SPLICING_MEASURING',
  COMMISSION: 'COMMISSION',
};

@Injectable({ providedIn: 'root' })
export class InstallationReportingMduApiService {
  constructor(private readonly http: HttpClient) {}

  getGroupAgreements(ifsNumber: string) {
    return this.http.get<PaginationResponse<GroupAgreementInstallationReport>>(
      `${installationReportingV3Url}/group-agreements/${ifsNumber}`,
    );
  }

  getGroupAgreementDetails(groupAgreementId: string) {
    return this.http.get<GroupAgreementDetails>(
      `${installationReportingV3Url}/group-agreements/${groupAgreementId}/details`,
    );
  }

  getBuildings(groupAgreementId: string) {
    return this.http.get<PaginationResponse<BuildingInstallationReport>>(
      `${installationReportingV3Url}/buildings/${groupAgreementId}`,
    );
  }

  getDeliveryPoints(ifsNumber: string, groupAgreementId: string) {
    return this.http.get<PaginationResponse<DeliveryPointInstallationReport>>(
      `${installationReportingV3Url}/delivery-points/${ifsNumber}/${groupAgreementId}`,
    );
  }

  markDeliveryPointBom(request: DeliveryPointBomRequest) {
    return this.http.put<void>(
      `${installationReportingV3Url}/delivery-points/bom`,
      request,
    );
  }

  getInstallationReportOverview(
    sourceId: string,
    ifsNumber: string,
    groupAgreementId: string,
    reportTypePath: string,
  ) {
    return this.http.post<InstallationReportOverview>(
      `${installationReportingV3Url}/${reportTypePath}`,
      { sourceId, ifsNumber, groupAgreementId },
    );
  }

  private getBuildingSections(installationReportId: string) {
    const url = `${installationReportingV3Url}/installation-reports`;
    const params = new HttpParams().set('includeSelfInspections', true);
    return forkJoin({
      plotDesign: this.http.get<PlotDesignV2Response>(
        `${url}/plot-design/${installationReportId}`,
        { params },
      ),
      preInstallation: this.http.get<PreInstallationV2Response>(
        `${url}/pre-installation/${installationReportId}`,
        { params },
      ),
      plotExcavation: this.http.get<PlotExcavationV2Response>(
        `${url}/plot-excavation/${installationReportId}`,
        { params },
      ),
      ductConnection: this.http.get<DuctConnectingV2Response>(
        `${url}/duct-connection/${installationReportId}`,
        { params },
      ),
      cableBlowing: this.http.get<CableBlowingV2Response>(
        `${url}/cable-blowing/${installationReportId}`,
        { params },
      ),
      splicingMeasuring: this.http.get<MeasuringV2Response>(
        `${url}/splicing-measuring/${installationReportId}`,
        { params },
      ),
      commissioning: this.http.get<CommissionV2Response>(
        `${url}/commissioning/${installationReportId}`,
        { params },
      ),
    });
  }

  private getDeliveryPointSections(installationReportId: string) {
    const url = `${installationReportingV3Url}/installation-reports`;
    const params = new HttpParams().set('includeSelfInspections', true);
    return forkJoin({
      preInstallation: this.http.get<PreInstallationV2Response>(
        `${url}/pre-installation/${installationReportId}`,
        { params },
      ),
      splicingMeasuring: this.http.get<MeasuringV2Response>(
        `${url}/splicing-measuring/${installationReportId}`,
        { params },
      ),
      commissioning: this.http.get<CommissionV2Response>(
        `${url}/commissioning/${installationReportId}`,
        { params },
      ),
    });
  }

  getInstallationReportSections(
    installationReportId: string,
    reportType: 'BUILDING' | 'DELIVERY_POINT',
  ) {
    return reportType === 'BUILDING'
      ? this.getBuildingSections(installationReportId)
      : this.getDeliveryPointSections(installationReportId);
  }

  getSelfInspectionDefinitions(
    section: InstallationReportSectionId,
    reportType: 'BUILDING' | 'DELIVERY_POINT' = 'BUILDING',
  ) {
    const definitionSection = definitionSectionBySection[section];
    if (!definitionSection) {
      return null;
    }
    const params = new HttpParams()
      .set('section', definitionSection)
      .set('isRemoved', false)
      .set('reportType', reportType);
    return this.http.get<
      PaginationResponse<SelfInspectionDefinitionV2Response>
    >(`${installationReportingV2Url}/self-inspection-definitions`, { params });
  }

  updateInstallationReport(
    installationReportId: string,
    request:
      | InstallationReportV2Request
      | DeliveryPointInstallationReportV2Request,
  ) {
    return this.http.put<void>(
      `${installationReportingV3Url}/installation-reports/${installationReportId}`,
      request,
    );
  }

  setSectionNotApplicable(
    request: SectionNotApplicableV3Request,
  ): Observable<SectionV2Response> {
    return this.http.patch<SectionV2Response>(
      `${installationReportingV3Url}/section/not-applicable`,
      request,
    );
  }

  patchPreInstallation(
    installationReportId: string,
    request: PatchPreInstallationV2Request,
  ) {
    return this.http.patch<PreInstallationV2Response>(
      `${installationReportingV3Url}/installation-reports/pre-installation/${installationReportId}`,
      request,
    );
  }

  completeSelfInspection(
    installationReportId: string,
    definitionId: string,
    request: SelfInspectionV2Request,
  ) {
    return this.http.put<SelfInspectionV2Response>(
      `${installationReportingV3Url}/self-inspection-answers/${installationReportId}/${definitionId}`,
      request,
    );
  }

  deleteSelfInspection(installationReportId: string, definitionId: string) {
    return this.http.delete<void>(
      `${installationReportingV3Url}/self-inspection-answers/${installationReportId}/${definitionId}`,
    );
  }

  uploadSelfInspectionFiles(
    installationReportId: string,
    definitionId: string,
    files: File[],
  ) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return this.http.post<ListWrapperEntity<FileMetaV2Response>>(
      `${installationReportingV3Url}/files/${installationReportId}/${definitionId}`,
      formData,
    );
  }

  deleteSelfInspectionFile(
    installationReportId: string,
    definitionId: string,
    fileId: string,
  ) {
    return this.http.delete<void>(
      `${installationReportingV3Url}/files/${installationReportId}/${definitionId}/${fileId}`,
    );
  }

  fetchExcel(ifsNumber: string) {
    return this.http
      .get(
        `${installationReportingV3Url}/installation-reports/${ifsNumber}/excel`,
        {
          responseType: 'blob',
          observe: 'response',
        },
      )
      .pipe(
        map((response: HttpResponse<Blob>) =>
          validateFileResponse(
            response.headers.get('Content-Disposition'),
            response.body,
          ),
        ),
      );
  }

  updateFromExcel(ifsNumber: string, formData: FormData) {
    return this.http.put<void>(
      `${installationReportingV3Url}/installation-reports/${ifsNumber}/excel`,
      formData,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      },
    );
  }

  getFile(fileId: string) {
    return this.http.get(
      `${installationReportingV3Url}/files/${fileId}/download`,
      {
        observe: 'response',
        responseType: 'blob',
      },
    );
  }
}
